import React, { useRef, useState, useEffect } from 'react';
import { Icons } from '@/src/modules/components/Icons';
import { Button } from '@/src/modules/components/Button';
import type { ApiInvitation } from '@/src/types';
import { useScreenshotShare } from '../hooks/useScreenshotShare';
import { TemplateRenderer, registry } from '@/src/modules/templates';
import { generateShareMessage } from '@/src/infrastructure/lib/share';
import { JsonTemplateRenderer } from '@/src/modules/renderer/JsonTemplateRenderer';
import { CinematicWeddingExperience } from '@/src/modules/experiences/wedding/CinematicWeddingExperience';
import { trackEvent } from '@/src/infrastructure/lib/analytics';


interface Props {
  invitation: ApiInvitation;
  onClose: () => void;
}

export function ShareInvitationModal({ invitation, onClose }: Props) {
  // Preview container — constrained to 1200×630 so the user sees the template scaled-down
  const containerRef = useRef<HTMLDivElement>(null);
  const previewWrapRef = useRef<HTMLDivElement>(null);
  const [previewScale, setPreviewScale] = useState(1);

  // Off-screen capture container — rendered at full 1200 px width, NO height
  // constraint, completely outside the viewport.  html-to-image captures this
  // to get the full natural height of the template (could be 2000+ px).
  const captureRef = useRef<HTMLDivElement>(null);

  // Measure wrapper width so the visible preview scales correctly
  useEffect(() => {
    const el = previewWrapRef.current;
    if (!el) return;
    const ro = new ResizeObserver(([entry]) => {
      setPreviewScale(entry.contentRect.width / 1200);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const {
    isGenerating,
    isGeneratingGif,
    gifProgress,
    error,
    downloadImage,
    downloadPdf,
    shareToWhatsApp,
    downloadGif,
  } = useScreenshotShare({
    // Preview element — used for the static PNG / WhatsApp image share
    elementRef: containerRef,
    // Off-screen element — used for GIF capture so we get the full height
    captureRef,
    fileName: `invitation-${invitation.eventName.replace(/\s+/g, '-').toLowerCase()}.png`,
    width: 1200,
    height: 630,
    quality: 1.0,
    pixelRatio: 2,
    invitationId: invitation.id,
    publicSlug: invitation.publicSlug ?? undefined,
  });

  const isBusy = isGenerating || isGeneratingGif;

  const handleShareWhatsApp = () => {
    // Build the guest link from the canonical hostedUrl, else the publicSlug.
    // Never fall back to invitation.id: the public endpoint is slug-only by
    // design (security finding M-02) and rejects raw ids with a 404. A share
    // action is only reachable for published/processing invitations, which
    // always have a publicSlug (assigned at creation).
    const link =
      invitation.hostedUrl ||
      (invitation.publicSlug ? `${window.location.origin}/i/${invitation.publicSlug}` : '');
    if (!link) return;
    const shareMessage = generateShareMessage(invitation, link);
    void shareToWhatsApp(
      invitation.eventName,
      shareMessage,
    );
  };

  const renderTemplateContent = () => {
    if (registry.has(invitation.templateId)) {
      return <TemplateRenderer templateId={invitation.templateId} data={invitation} />;
    }

    const template = invitation.template;
    if (template?.renderMode === 'json-renderer' && template.layoutSchema) {
      return (
        <JsonTemplateRenderer
          schema={template.layoutSchema}
          theme={template.themeConfig}
          data={invitation}
          mode="live"
        />
      );
    }

    if (invitation.occasionType === 'WEDDING') {
      return <CinematicWeddingExperience data={invitation} />;
    }

    return (
      <div
        className="w-full h-full flex flex-col items-center justify-center text-center px-8"
        style={{ background: 'linear-gradient(135deg, #fcf8ff, #e8d5c4)', color: '#574eb1' }}
      >
        <p className="text-sm uppercase tracking-[0.2em] font-bold">
          {invitation.occasionType.replace(/_/g, ' ')}
        </p>
        <p className="mt-4 text-4xl font-bold leading-tight">{invitation.eventName}</p>
        <p className="mt-4 text-xl">Hosted by {invitation.hostName}</p>
      </div>
    );
  };

  const gifLabel = isGeneratingGif
    ? gifProgress
      ? `Capturing ${gifProgress.current} / ${gifProgress.total}… (${gifProgress.estimatedSecsRemaining ?? '?'}s)`
      : 'Starting…'
    : 'Download GIF';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      {/*
       * ── Off-screen capture container ──────────────────────────────────────
       * Clipped visually via clip-path rather than left:-9999px so that the
       * browser still fully lays out and paints this subtree (off-screen fixed
       * elements can be skipped by GPU compositing).  html-to-image reads the
       * real rendered pixels, so the element must be painted.
       *
       * Critical: we also zero-out every CSS animation/transition here so that
       * Framer Motion whileInView sections (opacity:0 until scrolled into view)
       * are captured at their final visible state, not their invisible initial.
       * ─────────────────────────────────────────────────────────────────────
       */}
      <div
        aria-hidden="true"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '1200px',
          height: 'auto',
          minHeight: '630px',
          overflow: 'visible',
          pointerEvents: 'none',
          // clip so users never see it but the browser still paints it
          clipPath: 'inset(0 0 0 1200px)',
          backgroundColor: '#ffffff',
        }}
      >
        <div
          ref={captureRef}
          style={{
            width: '1200px',
            height: 'auto',
            minHeight: '630px',
            // Force all Framer Motion / CSS animations to their end state so
            // every section is visible when html-to-image captures the canvas.
            animation: 'none',
            transition: 'none',
          }}
        >
          {/* Inline style block forces descendant animations off without a
              class injection — this is the only reliable way inside a portal. */}
          <style>{`
            [data-capture-root] *,
            [data-capture-root] *::before,
            [data-capture-root] *::after {
              animation: none !important;
              transition: none !important;
              opacity: 1 !important;
              transform: none !important;
              visibility: visible !important;
            }
          `}</style>
          <div data-capture-root>
            {renderTemplateContent()}
          </div>
        </div>
      </div>

      {/* ── Visible modal ────────────────────────────────────────────────── */}
      <div className="bg-surface border border-outline-variant/30 w-full max-w-2xl rounded-3xl shadow-xl overflow-hidden flex flex-col">
        <header className="px-6 py-4 flex items-center justify-between border-b border-outline-variant/30">
          <div>
            <h2 className="text-lg font-bold text-on-surface">Share Invitation</h2>
            <p className="text-xs text-on-surface-variant">Generate a high-quality preview to share</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            className="p-2 rounded-full hover:bg-surface-low text-on-surface-variant disabled:opacity-40"
          >
            <Icons.X size={20} />
          </button>
        </header>

        <div className="p-6 flex flex-col md:flex-row gap-6">
          {/* Visible scaled preview */}
          <div className="flex-1 flex flex-col items-center justify-center bg-surface-lowest rounded-xl p-4 overflow-hidden">
            <div
              ref={previewWrapRef}
              className="relative border border-outline-variant/30 shadow-sm overflow-hidden w-full"
              style={{ aspectRatio: '1200/630' }}
            >
              <div
                className="absolute top-0 left-0 origin-top-left"
                style={{ width: '1200px', height: '630px', transform: `scale(${previewScale})` }}
              >
                {/* containerRef — used only for the visible preview scroll effect and static PNG export */}
                <div ref={containerRef} className="w-full h-full bg-surface-lowest relative overflow-y-auto scrollbar-none">
                  {renderTemplateContent()}
                </div>
              </div>
            </div>
            <p className="mt-2 text-[10px] text-on-surface-variant text-center">
              Live preview · scroll visible in GIF export
            </p>
          </div>

          {/* Actions */}
          <div className="w-full md:w-64 flex flex-col gap-3 justify-center">
            {error && (
              <p className="text-status-failed text-xs text-center mb-1">{error.message}</p>
            )}

            {/* WhatsApp */}
            <Button
              onClick={handleShareWhatsApp}
              disabled={isBusy}
              className="w-full flex items-center gap-2 justify-center bg-[#25D366] text-white hover:bg-[#128C7E]"
            >
              <Icons.WhatsApp size={18} />
              {isGenerating ? 'Generating…' : 'Share to WhatsApp'}
            </Button>

            {/* Download complete invitation as PDF */}
            <Button
              onClick={() => {
                trackEvent('PDF_DOWNLOADED', 'engagement', invitation.id);
                void downloadPdf();
              }}
              disabled={isBusy}
              variant="outline"
              className="w-full flex items-center gap-2 justify-center"
            >
              <Icons.FileText size={18} />
              Download PDF
            </Button>

            {/* Download static PNG */}
            <Button
              onClick={() => {
                trackEvent('QR_DOWNLOADED', 'engagement', invitation.id);
                void downloadImage();
              }}
              disabled={isBusy}
              variant="outline"
              className="w-full flex items-center gap-2 justify-center"
            >
              <Icons.Download size={18} />
              Download Image
            </Button>

            <Button
              onClick={() => {
                trackEvent('QR_DOWNLOADED', 'engagement', invitation.id);
                void downloadGif();
              }}
              disabled={isBusy}
              variant="outline"
              className="w-full flex items-center gap-2 justify-center"
            >
              {isGeneratingGif ? (
                <span className="w-4 h-4 rounded-full border-2 border-primary border-t-transparent animate-spin" />
              ) : (
                <Icons.Camera size={18} />
              )}
              {gifLabel}
            </Button>

            {/* GIF progress bar */}
            {isGeneratingGif && gifProgress && (
              <div className="w-full bg-outline-variant/20 rounded-full h-1.5 overflow-hidden">
                <div
                  className="h-full bg-primary transition-all duration-200"
                  style={{ width: `${(gifProgress.current / gifProgress.total) * 100}%` }}
                />
              </div>
            )}

            <p className="text-[10px] text-on-surface-variant text-center leading-snug">
              {gifProgress?.templateType === 'heavy'
                ? '🎬 Canvas animations detected — capturing in progress…'
                : gifProgress?.templateType === 'moderate'
                  ? '✨ CSS animations detected — capturing in progress…'
                  : '📸 Optimized: Static template, fast capture'}
              {!isGeneratingGif && ' • Click to auto-detect & generate'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
